import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  faTimes,
  faSave,
  faPlus,
  faMinus
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GetAdminService from "../../../services/admin/GetAdminService";
import CustomLoader from "../../customLoader/CustomLoader";
import { gradeData1 } from "../../../constants/data";
import swal from "sweetalert";
import "../AdminPages.css";
import "../Student/Student.css";

const emptyAssignment = () => ({
  grade: "",
  course: null,
  courseQuery: "",
  courseOptions: [],
  showCourseDropdown: false,
  courseLoading: false
});

const normalizeCourses = (data) => {
  const list =
    data?.data?.objects || data?.data?.result || data?.data?.course || [];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      const course = item?.course || item;
      return {
        id: course?.id,
        name: course?.name || course?.title || course?.course_name
      };
    })
    .filter((item) => item.id && item.name);
};

const CreateTeacher = ({ school, modify, onClose, onSave }) => {
  const courseTimeouts = useRef({});
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordError, setShowPasswordError] = useState("");
  const [assignments, setAssignments] = useState([emptyAssignment()]);

  useEffect(() => {
    if (!modify) return;
    setFirstName(modify.first_name || "");
    setLastName(modify.last_name || "");
    setEmail(modify.email || "");
    setMobile(modify.mobile || "");
    setAssignments(
      modify.assignments?.length
        ? modify.assignments.map((item) => ({
            ...emptyAssignment(),
            grade: item.grade || "",
            course: item.course || null,
            courseQuery: item.course?.name || ""
          }))
        : [emptyAssignment()]
    );
  }, [modify]);

  const updateAssignment = (index, patch) => {
    setAssignments((rows) =>
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
  };

  const applyCourseOptions = (index, data, value) => {
    const query = String(value || "").toLowerCase();
    const courses = normalizeCourses(data).filter((course) =>
      String(course.name).toLowerCase().includes(query)
    );
    updateAssignment(index, {
      courseOptions: courses,
      courseLoading: false,
      showCourseDropdown: true
    });
  };

  const searchCourses = (index, value) => {
    updateAssignment(index, {
      courseQuery: value,
      course: null,
      showCourseDropdown: !!value,
      courseLoading: !!value,
      courseOptions: []
    });
    clearTimeout(courseTimeouts.current[index]);
    if (!value) return;
    courseTimeouts.current[index] = setTimeout(() => {
      GetAdminService.searchCourses(value)
        .then((data) => {
          const courses = normalizeCourses(data);
          if (courses.length) {
            applyCourseOptions(index, data, value);
            return;
          }
          return GetAdminService.listOfCourse("broadcastcourse").then((all) =>
            applyCourseOptions(index, all, value)
          );
        })
        .catch(() => {
          GetAdminService.listOfCourse("broadcastcourse")
            .then((all) => applyCourseOptions(index, all, value))
            .catch(() => {
              updateAssignment(index, {
                courseOptions: [],
                courseLoading: false,
                showCourseDropdown: true
              });
            });
        });
    }, 600);
  };

  const selectCourse = (index, course) => {
    updateAssignment(index, {
      course,
      courseQuery: course.name,
      courseOptions: [],
      showCourseDropdown: false,
      courseLoading: false
    });
  };

  const addAssignment = () => {
    setAssignments((rows) => [...rows, emptyAssignment()]);
  };

  const removeAssignment = (index) => {
    setAssignments((rows) =>
      rows.length === 1 ? [emptyAssignment()] : rows.filter((_, i) => i !== index)
    );
  };

  const passwordValidation = () => {
    if (!password && modify) {
      setShowPasswordError("");
      return true;
    }
    if (!password || password.length < 8) {
      setShowPasswordError("Password must be greater than 8 characters");
      return false;
    }
    if (confirmPassword && confirmPassword !== password) {
      setShowPasswordError("Password and Confirm Password should be same.");
      return false;
    }
    setShowPasswordError("");
    return true;
  };

  const confirmPasswordValidation = (value) => {
    if (value !== password) {
      setShowPasswordError("Password and Confirm Password should be same.");
    } else if (password && password.length < 8) {
      setShowPasswordError("Password must be greater than 8 characters");
    } else {
      setShowPasswordError("");
    }
  };

  const assignmentsValid = assignments.every(
    (row) => row.grade && row.course?.id
  );
  const createPasswordValid = modify
    ? (!password && !confirmPassword) ||
      (password.length >= 8 && password === confirmPassword)
    : password &&
      confirmPassword &&
      password === confirmPassword &&
      password.length >= 8;
  const canSave =
    email &&
    mobile &&
    assignmentsValid &&
    createPasswordValid &&
    !showPasswordError;

  const saveButtonHandler = () => {
    if (!canSave) return;
    onSave({
      first_name: firstName,
      last_name: lastName,
      email,
      mobile,
      assignments: assignments.map((row) => ({
        grade: row.grade,
        course: row.course
      }))
    });
    swal({
      text: modify ? "Teacher updated successfully!" : "Teacher saved successfully!",
      icon: "success",
      buttons: { OK: true },
      closeOnClickOutside: true
    });
  };

  return (
    <Fragment>
      <div
        className="drawer-overlay"
        onClick={onClose}
      />
      <div className="create-student-container">
        <div className="drawer-header">
          <h3 className="drawer-title">
            {modify ? "Modify Teacher" : "New Teacher"}
          </h3>
          <button
            type="button"
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="drawer-section">
          <div className="drawer-form-row">
            <div className="form-label-col">Selected School</div>
            <div className="form-field-col">
              <div className="teacher-school-readonly">
                {school?.name || "No school selected"}
              </div>
            </div>
          </div>
        </div>

        <div className="drawer-section">
          <p className="drawer-section-label">Grade and course</p>
          {assignments.map((row, index) => (
            <div className="teacher-assignment-row" key={index}>
              <div className="teacher-assignment-fields">
                <div className="drawer-form-row">
                  <div className="form-label-col">
                    Select Grade
                    <span className="required-marker">*</span>
                  </div>
                  <div className="form-field-col">
                    <select
                      value={row.grade}
                      onChange={(e) =>
                        updateAssignment(index, { grade: e.target.value })
                      }
                    >
                      <option value="">Select Grade</option>
                      {gradeData1.map((item) => (
                        <option value={item.value} key={item.value}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="drawer-form-row">
                  <div className="form-label-col">
                    Course
                    <span className="required-marker">*</span>
                  </div>
                  <div className="form-field-col">
                    <input
                      type="text"
                      placeholder="Type to search course..."
                      value={row.courseQuery}
                      onChange={(e) => searchCourses(index, e.target.value)}
                      autoComplete="off"
                    />
                    {row.showCourseDropdown && (
                      <ul className="drawer-autocomplete">
                        {row.courseLoading && <CustomLoader />}
                        {!row.courseLoading &&
                          row.courseOptions.map((course) => (
                            <li
                              key={course.id}
                              onClick={() => selectCourse(index, course)}
                            >
                              {course.name}
                            </li>
                          ))}
                        {!row.courseLoading &&
                          row.courseOptions.length === 0 &&
                          row.courseQuery && (
                            <li>No courses found</li>
                          )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div className="teacher-assignment-actions">
                {index === assignments.length - 1 && (
                  <button
                    type="button"
                    className="teacher-assignment-icon-btn"
                    onClick={addAssignment}
                    title="Add grade and course"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                )}
                {assignments.length > 1 && (
                  <button
                    type="button"
                    className="teacher-assignment-icon-btn remove"
                    onClick={() => removeAssignment(index)}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="drawer-section">
          <p className="drawer-section-label">Personal information</p>
          <div className="drawer-form-row">
            <div className="form-label-col">First Name</div>
            <div className="form-field-col">
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
          </div>
          <div className="drawer-form-row">
            <div className="form-label-col">Last Name</div>
            <div className="form-field-col">
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="drawer-section">
          <p className="drawer-section-label">Contact</p>
          <div className="drawer-form-row">
            <div className="form-label-col">
              Email
              <span className="required-marker">*</span>
            </div>
            <div className="form-field-col">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
              />
            </div>
          </div>
          <div className="drawer-form-row">
            <div className="form-label-col">
              Mobile
              <span className="required-marker">*</span>
            </div>
            <div className="form-field-col">
              <input
                type="text"
                placeholder="+19999999999"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
              <p className="help-text">Hint: +13238511115</p>
            </div>
          </div>
        </div>

        <div className="drawer-section">
          <p className="drawer-section-label">User information</p>
          <div className="drawer-form-row">
            <div className="form-label-col">
              Password
              {!modify && <span className="required-marker">*</span>}
            </div>
            <div className="form-field-col">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={passwordValidation}
              />
            </div>
          </div>
          <div className="drawer-form-row">
            <div className="form-label-col">
              Confirm Password
              {!modify && <span className="required-marker">*</span>}
            </div>
            <div className="form-field-col">
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => confirmPasswordValidation(confirmPassword)}
              />
            </div>
          </div>
          {showPasswordError && (
            <p className="drawer-password-error">{showPasswordError}</p>
          )}
        </div>

        <div className="drawer-footer">
          <button
            type="button"
            onClick={saveButtonHandler}
            disabled={!canSave}
            className="drawer-btn-save"
          >
            <FontAwesomeIcon icon={faSave} />
            Save
          </button>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateTeacher;
